import { PropertyId, createDynamicPropertyId } from "./PropertyId";
import PropertyItem from "./PropertyItem";

export default class PropertyList {
    #items: PropertyItem<any>[] = [];
    #dynamicIdCounter = 0;

    getProperty<T = unknown>(id: PropertyId): PropertyItem<T> | undefined {
        return this.#items.find(item => item.id === id) as PropertyItem<T> | undefined;
    }

    getOrCreateProperty<T>(id: PropertyId, factory: () => PropertyItem<T>): PropertyItem<T> {
        const existing = this.getProperty<T>(id);
        if (existing) return existing;

        const created = factory();
        this.addProperty(created);
        return created;
    }

    addProperty(item: PropertyItem<any>): void {
        this.#items.push(item);
    }

    deleteProperty(id: PropertyId): void {
        this.#items = this.#items.filter(item => item.id !== id);
    }

    // Unique within this PropertyList's lifetime only (see PropertyId.ts).
    createDynamicId(_owner: unknown): PropertyId {
        this.#dynamicIdCounter += 1;
        return createDynamicPropertyId(this.#dynamicIdCounter);
    }

    getItems(): PropertyItem<any>[] {
        return [...this.#items];
    }

    // Reduces `this` to the intersection of ids common with `other`, flagging `varying = true`
    // wherever the two sides disagree on value. Folding this pairwise across N selected objects'
    // lists yields the common-property-set-with-varying-flags semantics used by the query pipeline.
    merge(other: PropertyList): void {
        this.#items = this.#items.filter(item => {
            const otherItem = other.getProperty(item.id);
            if (!otherItem) return false;

            const equal = item.translator.isEqual(item.value, otherItem.value);
            item.varying = item.varying || otherItem.varying || !equal;
            return true;
        });
    }
}
